/**
 * GeminiAgentRuntime — Gemini native function calling agent loop.
 *
 * Replaces JSON-mode approach with proper functionDeclarations/functionCall/functionResponse
 * protocol. Supports multi-turn, programmatic confirmation, error formatting,
 * auto-retry on network errors, streaming, and tool execution timeout.
 */

import type { WebMCPTool } from "../core/tool-registry"
import type { AgentPersona } from "./persona/types"
import { requiresConfirmation, buildConfirmation, type ConfirmationResult } from "./confirmation-policy"
import { formatToolError, isRetryable } from "./error-formatter"
import type { LlmDriver, LlmGenerateResult } from "./llm-driver"

// ── Types ───────────────────────────────────────────────────

export type AgentContentPart =
  | { type: "text"; text: string }
  | {
      type: "tool-call"
      toolName: string
      args: Record<string, unknown>
      result: unknown
      success: boolean
      error?: string
    }

export interface AgentRunResult {
  contentParts: AgentContentPart[]
  confirmation?: ConfirmationResult
}

export interface AgentToolCallOptions {
  signal: AbortSignal
  attempt: number
  idempotencyKey?: string
}

export type AgentToolExecutor = (
  name: string,
  params: Record<string, unknown>,
  options?: AgentToolCallOptions,
) => Promise<unknown>

interface GeminiPart {
  text?: string
  functionCall?: { id?: string; name: string; args: Record<string, unknown> }
  functionResponse?: { id?: string; name: string; response: Record<string, unknown> }
}

interface GeminiContent {
  role: "user" | "model"
  parts: GeminiPart[]
}

interface GeminiAgentConfig {
  apiKey: string
  model?: string
  /**
   * Injectable LLM driver. Required for any network call. Omitting it uses a
   * no-network fail-closed stub — the production default must not construct
   * GeminiLlmDriver or ClaudeLlmDriver. Tests that need the Gemini REST path
   * inject GeminiLlmDriver explicitly.
   */
  llmDriver?: LlmDriver
}

// ── Constants ───────────────────────────────────────────────

const MAX_STEPS = 10
const DEFAULT_MODEL = "gemini-3-flash-preview"
const TOOL_TIMEOUT_MS = 30_000

/** No-network default. Throws on generate; never fetches. Not an Echo driver. */
const FAIL_CLOSED_LLM_DRIVER: LlmDriver = {
  async generate(): Promise<LlmGenerateResult> {
    throw new Error("GeminiAgentRuntime requires an injected llmDriver")
  },
}

// ── Runtime ─────────────────────────────────────────────────

export class GeminiAgentRuntime {
  private apiKey: string
  private model: string
  private llmDriver: LlmDriver
  private contents: GeminiContent[] = []
  private pendingConfirmation?: {
    tool: WebMCPTool
    args: Record<string, unknown>
    callId: string
    callTool: AgentToolExecutor
    persona: AgentPersona
    tools: WebMCPTool[]
    contentParts: AgentContentPart[]
    functionDeclarations: Array<{ name: string; description: string; parameters: unknown }>
    toolMap: Map<string, WebMCPTool>
  }

  constructor(config: GeminiAgentConfig) {
    this.apiKey = config.apiKey
    this.model = config.model ?? DEFAULT_MODEL
    // Omitting llmDriver yields FAIL_CLOSED_LLM_DRIVER (throw, no network).
    // Production default must not construct GeminiLlmDriver.
    this.llmDriver = config.llmDriver ?? FAIL_CLOSED_LLM_DRIVER
  }

  /** Get the conversation contents for bridge integration. */
  getContents(): GeminiContent[] {
    return this.contents
  }

  /** Restore conversation history (e.g. from sessionStorage). */
  setContents(contents: GeminiContent[]) {
    this.contents = contents
  }

  /**
   * Run the agent loop: send user message, process function calls,
   * return text + tool-call parts.
   *
   * @param onChunk - Optional callback for streaming text chunks
   */
  async run(
    userMessage: string,
    tools: WebMCPTool[],
    persona: AgentPersona,
    callTool: AgentToolExecutor,
    onChunk?: (text: string) => void,
  ): Promise<AgentRunResult> {
    // Filter tools by persona
    const filteredTools = tools.filter((t) => persona.toolFilter(t))

    const functionDeclarations = filteredTools.map((t) => ({
      name: t.name,
      description: t.description,
      parameters: t.inputSchema,
    }))

    // Trim history to prevent oversized requests (keep last 20 turns max)
    const MAX_HISTORY = 20
    if (this.contents.length > MAX_HISTORY) {
      this.contents = this.contents.slice(-MAX_HISTORY)
    }

    // Append user message to existing conversation (multi-turn support)
    this.contents.push({ role: "user", parts: [{ text: userMessage }] })

    const contentParts: AgentContentPart[] = []
    const toolMap = new Map(filteredTools.map((t) => [t.name, t]))

    for (let step = 0; step < MAX_STEPS; step++) {
      // Use streaming when onChunk callback provided, otherwise standard call
      let parts: GeminiPart[]
      let modelRole: string

      if (onChunk && this.llmDriver.generateStream) {
        const streamResult = await this.llmDriver.generateStream({
          systemPrompt: persona.systemPrompt,
          toolDecls: functionDeclarations,
          contents: this.contents,
          onChunk,
        })
        parts = streamResult.parts
        modelRole = streamResult.role
      } else {
        const response = await this.llmDriver.generate({
          systemPrompt: persona.systemPrompt,
          toolDecls: functionDeclarations,
          contents: this.contents,
        })
        parts = response.parts
        modelRole = response.role
      }

      // Add model response to conversation
      this.contents.push({ role: modelRole as "model", parts })

      // Separate text parts and function call parts
      const textParts = parts.filter((p: GeminiPart) => p.text)
      const fcParts = parts.filter((p: GeminiPart) => p.functionCall)

      // If no function calls, this is the final answer
      if (fcParts.length === 0) {
        for (const tp of textParts) {
          contentParts.push({ type: "text", text: tp.text! })
        }
        return { contentParts }
      }

      // Process each function call
      for (const fcPart of fcParts) {
        const fc = fcPart.functionCall!
        const tool = toolMap.get(fc.name)
        const callId = fc.id ?? `auto-${step}`

        if (!tool) {
          // Tool not found — send error response
          const errResponse = { error: `Tool "${fc.name}" not available` }
          this.contents.push({
            role: "user",
            parts: [{ functionResponse: { id: callId, name: fc.name, response: errResponse } }],
          })
          contentParts.push({
            type: "tool-call",
            toolName: fc.name,
            args: fc.args ?? {},
            result: null,
            success: false,
            error: `Tool "${fc.name}" not available`,
          })
          continue
        }

        // Check confirmation policy
        if (requiresConfirmation(tool, fc.args)) {
          const confirmation = buildConfirmation(tool, fc.args ?? {}, callId)
          this.pendingConfirmation = {
            tool,
            args: fc.args ?? {},
            callId,
            callTool,
            persona,
            tools,
            contentParts,
            functionDeclarations,
            toolMap,
          }
          return { contentParts, confirmation }
        }

        // Auto-execute the tool with retry and timeout
        await this.executeToolWithRetry(
          tool,
          fc.args ?? {},
          callId,
          callTool,
          contentParts,
        )
      }
    }

    // MAX_STEPS exceeded
    contentParts.push({
      type: "text",
      text: "I've taken several actions but couldn't complete the request. Could you try breaking it into smaller steps?",
    })
    return { contentParts }
  }

  /**
   * Resume after user confirms or rejects a pending tool execution.
   */
  async resumeAfterConfirmation(
    callId: string,
    approved: boolean,
  ): Promise<AgentRunResult> {
    if (!this.pendingConfirmation || this.pendingConfirmation.callId !== callId) {
      return {
        contentParts: [{ type: "text", text: "No pending confirmation found." }],
      }
    }

    const {
      tool,
      args,
      callTool,
      contentParts,
      persona,
      functionDeclarations,
      toolMap,
    } = this.pendingConfirmation
    this.pendingConfirmation = undefined

    if (!approved) {
      // Send rejection to Gemini and continue the conversation loop
      this.contents.push({
        role: "user",
        parts: [{
          functionResponse: {
            id: callId,
            name: tool.name,
            response: { rejected: true, reason: "User rejected this action" },
          },
        }],
      })

      // Continue the loop so Gemini can suggest alternatives
      return this.continueLoop(contentParts, callTool, persona, functionDeclarations, toolMap)
    }

    // Execute the approved tool with timeout
    await this.executeToolWithRetry(
      tool,
      args,
      callId,
      callTool,
      contentParts,
    )

    // Continue the loop after execution
    return this.continueLoop(contentParts, callTool, persona, functionDeclarations, toolMap)
  }

  // ── Private ─────────────────────────────────────────────

  /**
   * Execute a tool with an abortable timeout. Read-only calls may retry once;
   * mutations require an explicit safe retry policy and idempotency key.
   */
  private async executeToolWithRetry(
    tool: WebMCPTool,
    args: Record<string, unknown>,
    callId: string,
    callTool: AgentToolExecutor,
    contentParts: AgentContentPart[],
  ): Promise<void> {
    let retried = false
    const idempotencyKey = readIdempotencyKey(tool, args)
    const retryAllowed = canRetryTool(tool, idempotencyKey)
    const timeoutMs = normalizeTimeoutMs(tool.metadata?.timeoutMs)

    const attempt = async (): Promise<void> => {
      const attemptNumber = retried ? 2 : 1
      const controller = new AbortController()
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      try {
        const options: AgentToolCallOptions = {
          signal: controller.signal,
          attempt: attemptNumber,
          ...(idempotencyKey ? { idempotencyKey } : {}),
        }
        const toolCall =
          callTool.length >= 3
            ? callTool(tool.name, args, options)
            : callTool(tool.name, args)
        const result = await Promise.race([
          toolCall,
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
              controller.abort(new Error(`Tool timed out (${timeoutMs}ms)`))
              reject(new Error(`Tool timed out (${timeoutMs}ms)`))
            }, timeoutMs)
          }),
        ])
        contentParts.push({
          type: "tool-call",
          toolName: tool.name,
          args,
          result,
          success: true,
        })
        this.contents.push({
          role: "user",
          parts: [{
            functionResponse: {
              id: callId,
              name: tool.name,
              response: typeof result === 'object' && result !== null ? result as Record<string, unknown> : { result: result ?? true },
            },
          }],
        })
      } catch (err) {
        // Retry once for retryable errors
        if (retryAllowed && isRetryable(err) && !retried) {
          retried = true
          return attempt()
        }

        const formatted = formatToolError(err, tool.name)
        contentParts.push({
          type: "tool-call",
          toolName: tool.name,
          args,
          result: null,
          success: false,
          error: formatted.message,
        })
        this.contents.push({
          role: "user",
          parts: [{
            functionResponse: {
              id: callId,
              name: tool.name,
              response: { error: formatted.message },
            },
          }],
        })
      } finally {
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    await attempt()
  }

  /**
   * Continue the agent loop after confirmation resolution.
   * Sends conversation to Gemini for the next response.
   */
  private async continueLoop(
    contentParts: AgentContentPart[],
    callTool: AgentToolExecutor,
    persona: AgentPersona,
    functionDeclarations: Array<{ name: string; description: string; parameters: unknown }>,
    toolMap: Map<string, WebMCPTool>,
  ): Promise<AgentRunResult> {
    // One more step to let the model respond after approval/rejection
    const response = await this.llmDriver.generate({
      systemPrompt: persona.systemPrompt,
      toolDecls: functionDeclarations,
      contents: this.contents,
    })
    const parts = response.parts
    const modelRole = response.role

    this.contents.push({ role: modelRole as "model", parts })

    const textParts = parts.filter((p: GeminiPart) => p.text)
    const fcParts = parts.filter((p: GeminiPart) => p.functionCall)

    if (fcParts.length === 0) {
      for (const tp of textParts) {
        contentParts.push({ type: "text", text: tp.text! })
      }
      return { contentParts }
    }

    // Process any follow-up function calls
    for (const fcPart of fcParts) {
      const fc = fcPart.functionCall!
      const tool = toolMap.get(fc.name)
      const callId = fc.id ?? "resume-auto"

      if (!tool) {
        this.contents.push({
          role: "user",
          parts: [{ functionResponse: { id: callId, name: fc.name, response: { error: `Tool "${fc.name}" not available` } } }],
        })
        continue
      }

      // ★NEW #405 gate — identical shape to initial-call gate 181-194
      if (requiresConfirmation(tool, fc.args)) {
        const confirmation = buildConfirmation(tool, fc.args ?? {}, callId)
        this.pendingConfirmation = {
          tool,
          args: fc.args ?? {},
          callId,
          callTool,
          persona,
          tools: Array.from(toolMap.values()),
          contentParts,
          functionDeclarations,
          toolMap,
        }
        return { contentParts, confirmation }
      }

      await this.executeToolWithRetry(tool, fc.args ?? {}, callId, callTool, contentParts)
    }

    // Get final text response
    const finalResp = await this.llmDriver.generate({
      systemPrompt: persona.systemPrompt,
      toolDecls: functionDeclarations,
      contents: this.contents,
    })
    const finalParts = finalResp.parts
    this.contents.push({ role: "model", parts: finalParts })

    for (const p of finalParts) {
      if (p.text) contentParts.push({ type: "text", text: p.text })
    }

    return { contentParts }
  }
}

function normalizeTimeoutMs(value: number | undefined): number {
  if (!Number.isFinite(value) || !value || value <= 0) return TOOL_TIMEOUT_MS
  return Math.min(Math.max(Math.trunc(value), 100), 120_000)
}

function readIdempotencyKey(
  tool: WebMCPTool,
  args: Record<string, unknown>,
): string | undefined {
  const param = tool.metadata?.idempotencyKeyParam
  if (!param) return undefined
  const value = args[param]
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function canRetryTool(tool: WebMCPTool, idempotencyKey: string | undefined): boolean {
  if (tool.metadata?.retryPolicy === "never") return false
  if (tool.metadata?.readOnly) return true
  return tool.metadata?.retryPolicy === "safe" && Boolean(idempotencyKey)
}
